import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';
import Motivo from '../experiencia/Motivo';

/* =====================================================================
   Planta · TC-14 (turnos), TC-18 (planes en estación), TC-15 (PPM)
   =====================================================================
   Lo que se declaraba con un INSERT. Tres bloques y un gráfico; nada
   decide aquí: el turno lo resuelve la sesión al abrir, el plan lo
   pide la terminal a su ritmo, y el PPM sale de lo contado.
   ===================================================================== */
const DIAS = [[1, 'L'], [2, 'M'], [3, 'X'], [4, 'J'], [5, 'V'], [6, 'S'], [7, 'D']];

export default function Planta() {
  const avisos = useAvisos();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [turnos, setTurnos] = useState(null);
  const [asig, setAsig] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [ubic, setUbic] = useState([]);
  const [gente, setGente] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get('/planta/turnos?todos=true'), api.get('/planta/asignaciones'), api.get('/planta/planes'),
      api.get('/ubicaciones').catch(() => []), api.get('/usuarios').catch(() => []),
      api.get('/inspecciones/planes').catch(() => []),
    ]).then(([t, a, p, u, g, c]) => {
      if (!vivo) return;
      setTurnos(t); setAsig(a); setPlanes(p);
      setUbic(Array.isArray(u) ? u : (u.filas || []));
      setGente(Array.isArray(g) ? g : (g.filas || []));
      setCatalogo(Array.isArray(c) ? c : (c.filas || []));
      setError('');
    }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function accion(fn) {
    try { const r = await fn(); avisos.exito(r.mensaje || 'Hecho.'); recargar(); }
    catch (e) { avisos.error(e.message); }
  }

  if (error && !turnos) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!turnos) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo la planta…
      </p>
    );
  }
  return (
    <div className="flex max-w-5xl flex-col gap-6 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Terminales en planta</h1>
        <p className="text-gray-600">Turnos, quién trabaja en qué estación, qué plan corre en cada una, y el PPM.</p>
        <Ayuda id="terminal.planta.pantalla" />
      </header>
      <Turnos turnos={turnos} accion={accion} />
      <Asignaciones asig={asig} turnos={turnos.filter((t) => t.activo)} ubic={ubic} gente={gente} accion={accion} />
      <Planes planes={planes} ubic={ubic} catalogo={catalogo} accion={accion} />
      <Ppm ubic={ubic} />
    </div>
  );
}

function Turnos({ turnos, accion }) {
  const [f, setF] = useState({ nombre: '', inicio: '06:00', fin: '14:00', dias: [1, 2, 3, 4, 5] });
  const listo = f.nombre.trim().length >= 3 && f.inicio && f.fin && f.inicio !== f.fin && f.dias.length > 0;
  const alternar = (d) => setF({ ...f, dias: f.dias.includes(d) ? f.dias.filter((x) => x !== d) : [...f.dias, d] });
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">Turnos</h2>
      <Ayuda id="terminal.planta.turno" />
      {turnos.length === 0 ? (
        <NotaExplicativa titulo="Todavía no hay turnos">Sin turnos, las sesiones no saben en cuál caen y el PPM no se separa por turno. Declara el primero abajo.</NotaExplicativa>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">
          {turnos.map((t) => (
            <li key={t.id} className={`flex flex-wrap items-center gap-x-3 py-1 ${t.activo ? '' : 'text-gray-400'}`}>
              <span className="font-medium">{t.nombre}</span>
              <span>{String(t.inicio).slice(0, 5)}–{String(t.fin).slice(0, 5)}</span>
              <span className="font-mono text-xs">{DIAS.filter(([n]) => (t.dias || []).includes(n)).map(([, l]) => l).join('')}</span>
              {t.sucursal && <span className="text-gray-500">{t.sucursal}</span>}
              <span className="text-gray-500">{t.asignados} asignado(s)</span>
              <button type="button" onClick={() => accion(() => api.patch(`/planta/turnos/${t.id}`, { activo: !t.activo }))}
                      className="ml-auto text-xs font-semibold text-[var(--color-marca)]">{t.activo ? 'Apagar' : 'Encender'}</button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={(e) => { e.preventDefault(); accion(() => api.post('/planta/turnos', { nombre: f.nombre.trim(), inicio: f.inicio, fin: f.fin, dias: f.dias })).then(() => setF({ ...f, nombre: '' })); }}
            className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-sm">Nombre *<input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Matutino" className="rounded-lg border border-gray-300 p-2" /></label>
        <label className="flex flex-col text-sm">Empieza<input type="time" value={f.inicio} onChange={(e) => setF({ ...f, inicio: e.target.value })} className="rounded-lg border border-gray-300 p-2" /></label>
        <label className="flex flex-col text-sm">Termina<input type="time" value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} className="rounded-lg border border-gray-300 p-2" /></label>
        <div className="flex gap-1">{DIAS.map(([n, l]) => (
          <button key={n} type="button" onClick={() => alternar(n)} aria-pressed={f.dias.includes(n)}
                  className={`h-9 w-9 rounded-lg border text-sm ${f.dias.includes(n) ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)] font-semibold' : 'border-gray-200'}`}>{l}</button>
        ))}</div>
        <button type="submit" disabled={!listo} className="inline-flex items-center gap-1 rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300"><Plus className="h-4 w-4" aria-hidden="true" /> Crear turno</button>
        {!listo && <span className="text-xs text-amber-700">Nombre de tres letras o más, horas distintas y al menos un día.</span>}
      </form>
    </section>
  );
}

function Asignaciones({ asig, turnos, ubic, gente, accion }) {
  const [f, setF] = useState({ id_ubicacion: '', id_turno: '', id_operador: '' });
  const listo = f.id_ubicacion && f.id_turno && f.id_operador;
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">Quién trabaja en qué estación</h2>
      <p className="text-sm text-gray-600">Si alguien abre sesión fuera de su asignación, el gemelo de la estación lo marca; no se le impide.</p>
      {asig.length === 0 ? <p className="mt-2 text-sm text-gray-600">Nadie asignado todavía.</p> : (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">{asig.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-3 py-1">
            <span className="font-mono">{a.estacion}</span><span className="text-gray-600">{a.estacion_nombre}</span>
            <span>{a.turno}</span><span className="font-medium">{a.operador}</span>
            <button type="button" onClick={() => accion(() => api.post(`/planta/asignaciones/${a.id}/baja`))} className="ml-auto text-xs text-gray-600">Quitar</button>
          </li>))}</ul>
      )}
      <form onSubmit={(e) => { e.preventDefault(); accion(() => api.post('/planta/asignaciones', { id_ubicacion: Number(f.id_ubicacion), id_turno: Number(f.id_turno), id_operador: Number(f.id_operador) })); }}
            className="mt-3 flex flex-wrap items-end gap-2">
        <select value={f.id_ubicacion} onChange={(e) => setF({ ...f, id_ubicacion: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm"><option value="">Estación…</option>{ubic.map((u) => <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>)}</select>
        <select value={f.id_turno} onChange={(e) => setF({ ...f, id_turno: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm"><option value="">Turno…</option>{turnos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}</select>
        <select value={f.id_operador} onChange={(e) => setF({ ...f, id_operador: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm"><option value="">Persona…</option>{gente.map((g) => <option key={g.id} value={g.id}>{g.nombre_completo || g.nombre || g.email}</option>)}</select>
        <button type="submit" disabled={!listo} className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Asignar</button>
        {/* EX-1 (parche del integrador en el merge, decisión del dueño): el botón apagado dice por qué. */}
        {!listo && <span className="text-xs text-amber-700">Elige estación, turno y persona.</span>}
      </form>
    </section>
  );
}

function Planes({ planes, ubic, catalogo, accion }) {
  const [f, setF] = useState({ id_ubicacion: '', id_plan: '', cada_piezas: '', cada_minutos: '' });
  const listo = f.id_ubicacion && f.id_plan && (Number(f.cada_piezas) > 0 || Number(f.cada_minutos) > 0);
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">Qué plan de inspección corre en cada estación</h2>
      <Ayuda id="terminal.planta.plan" />
      {planes.length === 0 ? <p className="mt-2 text-sm text-gray-600">Ningún plan puesto en una estación. La terminal no pedirá inspecciones hasta que haya uno.</p> : (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">{planes.map((p) => (
          <li key={p.id} className={`flex flex-wrap items-center gap-x-3 py-1 ${p.activo ? '' : 'text-gray-400'}`}>
            <span className="font-mono">{p.estacion}</span><span className="font-medium">{p.plan}</span>
            <span>{[p.cada_piezas && `cada ${p.cada_piezas} piezas`, p.cada_minutos && `cada ${p.cada_minutos} min`].filter(Boolean).join(' y ')}</span>
            {p.activo && <button type="button" onClick={() => accion(() => api.post(`/planta/planes/${p.id}/apagar`))} className="ml-auto text-xs text-gray-600">Apagar</button>}
          </li>))}</ul>
      )}
      <form onSubmit={(e) => { e.preventDefault(); accion(() => api.put('/planta/planes', { id_ubicacion: Number(f.id_ubicacion), id_plan: Number(f.id_plan), cada_piezas: f.cada_piezas ? Number(f.cada_piezas) : null, cada_minutos: f.cada_minutos ? Number(f.cada_minutos) : null })); }}
            className="mt-3 flex flex-wrap items-end gap-2">
        <select value={f.id_ubicacion} onChange={(e) => setF({ ...f, id_ubicacion: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm"><option value="">Estación…</option>{ubic.map((u) => <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>)}</select>
        <select value={f.id_plan} onChange={(e) => setF({ ...f, id_plan: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm"><option value="">Plan…</option>{catalogo.map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.sku ? ` · ${c.sku}` : ''}</option>)}</select>
        <input type="number" min="1" value={f.cada_piezas} onChange={(e) => setF({ ...f, cada_piezas: e.target.value })} placeholder="Cada N piezas" className="w-36 rounded-lg border border-gray-300 p-2 text-sm" />
        <input type="number" min="1" value={f.cada_minutos} onChange={(e) => setF({ ...f, cada_minutos: e.target.value })} placeholder="Cada N minutos" className="w-36 rounded-lg border border-gray-300 p-2 text-sm" />
        <button type="submit" disabled={!listo} aria-describedby={listo ? undefined : 'motivo-poner-plan'} className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Poner plan</button>
        <Motivo id="motivo-poner-plan" cuando={!listo} className="basis-full">Elige la estación y el plan, y di cada cuántas piezas o cada cuántos minutos se revisa.</Motivo>
      </form>
    </section>
  );
}

export function Ppm({ ubic, idUbicacion: fija }) {
  const [id, setId] = useState(fija ? String(fija) : '');
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    const q = (fija || id) ? `?id_ubicacion=${fija || id}` : '';
    api.get(`/planta/ppm${q}`).then((x) => { if (vivo) setD(x); }).catch(() => { if (vivo) setD({ serie: [], dice: 'No se pudo leer el PPM.' }); });
    return () => { vivo = false; };
  }, [id, fija]);
  const serie = (d && d.serie) || [];
  const max = Math.max(1, ...serie.map((s) => s.ppm || 0));
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">PPM de los últimos 30 días</h2>
      <Ayuda id="terminal.planta.ppm" />
      {!fija && ubic && (
        <select value={id} onChange={(e) => setId(e.target.value)} className="mt-2 rounded-lg border border-gray-300 p-2 text-sm">
          <option value="">Toda la planta</option>{ubic.map((u) => <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>)}
        </select>
      )}
      {d && serie.length === 0 ? <p className="mt-2 text-sm text-gray-600">{d.dice}</p> : (
        <div className="mt-3 flex h-40 items-end gap-1 overflow-x-auto" role="img" aria-label="PPM por día">
          {serie.map((s) => (
            <div key={s.dia} className="flex flex-col items-center gap-1" title={`${s.dia}: ${s.ppm == null ? 'sin PPM' : `${s.ppm} PPM`} · buenas ${s.buenas} · malas ${s.rechazadas}`}>
              <div className={`w-4 rounded-t ${s.ppm == null ? 'bg-gray-200' : s.ppm > 10000 ? 'bg-red-500' : 'bg-[var(--color-marca)]'}`}
                   style={{ height: `${s.ppm == null ? 4 : Math.max(4, Math.round((s.ppm / max) * 120))}px` }} />
              <span className="text-[10px] text-gray-500">{String(s.dia).slice(8, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
